import { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { ChevronDown, ChevronRight, History } from "lucide-react";

const th = "text-left text-[10px] uppercase tracking-widest text-slate-400 font-semibold px-3 py-2.5";
const td = "px-3 py-2.5 text-[13px] text-slate-700";

const parsePair = (s) => {
  const m = (s || "").match(/([\d.]+)\s*[xX*×]\s*([\d.]+)/);
  if (!m) return null;
  const a = +m[1], b = +m[2];
  return { id: Math.min(a, b), od: Math.max(a, b) };
};

const MarginBadge = ({ v }) => v == null ? <span className="text-slate-300">—</span> : (
  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${v >= 25 ? "bg-emerald-50 text-emerald-700" : v >= 12 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-600"}`}>{v}%</span>
);

export const LeadSizeIntel = ({ items }) => {
  const keys = useMemo(() => {
    const seen = new Set();
    return (items || []).map((it) => {
      const p = parsePair(it.size_input || it.size_label || "");
      if (!p) return null;
      const k = `${p.id}x${p.od}|${it.category || ""}`;
      if (seen.has(k)) return null;
      seen.add(k);
      return { ...p, category: it.category || "" };
    }).filter(Boolean);
  }, [items]);
  const sig = keys.map((k) => `${k.id}x${k.od}|${k.category}`).join(",");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!sig) { setRows([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await Promise.all(keys.map((k) =>
          api.get(`/size-intel/search?id_mm=${k.id}&od_mm=${k.od}${k.category ? `&category=${encodeURIComponent(k.category)}` : ""}`)));
        const all = res.flatMap((r) => r.data.rows || []);
        const seen = new Set(); const out = [];
        for (const r of all) {
          const k = `${r.source}|${r.number}|${r.size}|${r.sales_rate}|${r.date}`;
          if (!seen.has(k)) { seen.add(k); out.push(r); }
        }
        out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setRows(out.slice(0, 15));
      } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sig || rows.length === 0) return null;
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="lead-size-intel">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors duration-150 text-left"
              data-testid="lead-size-intel-toggle">
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <History size={13} className="text-[#3B82F6]" />
        <span className="text-xs font-semibold text-slate-700">Size History</span>
        <span className="text-[10px] font-bold bg-[#3B82F6]/10 text-[#3B82F6] rounded-full px-2 py-0.5">{rows.length} record{rows.length === 1 ? "" : "s"}</span>
        <span className="text-[11px] text-slate-400 ml-auto">past purchases for this size</span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead className="bg-slate-50 border-y border-slate-100">
              <tr><th className={th}>Product</th><th className={th}>Size</th><th className={th}>Customer</th><th className={th}>Date</th>
                  <th className={`${th} text-right`}>Qty</th><th className={`${th} text-right`}>Purchase (₹)</th>
                  <th className={th}>Margin %</th><th className={`${th} text-right`}>Sales Rate</th><th className={th}>Ref</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-50" data-testid="lead-size-intel-rows">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50/60">
                  <td className={`${td} font-medium`}>{r.product}{r.color && <span className="text-slate-400"> · {r.color}</span>}</td>
                  <td className={td}>{r.size}</td>
                  <td className={td}>{r.customer}</td>
                  <td className={td}>{fmtDate(r.date)}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.quantity} {r.unit}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.purchase_rate ? `₹${r.purchase_rate}` : "—"}</td>
                  <td className={td}><MarginBadge v={r.margin_pct} /></td>
                  <td className={`${td} text-right tabular-nums font-semibold`}>₹{r.sales_rate}</td>
                  <td className={td}><span className="text-[10px] uppercase tracking-wider bg-slate-100 rounded px-1.5 py-0.5 text-slate-500">{r.source}</span> <span className="text-xs text-slate-400">{r.number}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
